import create from 'zustand'

// Session storage key for chatbot state
const CHATBOT_STORAGE_KEY = 'chatbot_session_state'

// Helper functions for session storage
const saveToSessionStorage = (state) => {
  try {
    // Only persist essential chat state, exclude UI-specific state
    const stateToPersist = {
      chatId: state.chatId,
      chatConversationState: state.chatConversationState,
      chatMessageHistory: state.chatMessageHistory,
      inputValue: state.inputValue,
      originalTools: state.originalTools,
      currentActionMessage: state.currentActionMessage,
      executeToolAction: state.executeToolAction,
      // Cache data
      nameToId: state.nameToId,
      idToName: state.idToName,
      cache: state.cache,
      diseaseNameToIdMapping: state.diseaseNameToIdMapping,
      diseaseIdToNameMapping: state.diseaseIdToNameMapping,
    }
    sessionStorage.setItem(CHATBOT_STORAGE_KEY, JSON.stringify(stateToPersist))
  } catch (error) {
    console.error('Error saving chatbot state to sessionStorage:', error)
  }
}

const loadFromSessionStorage = () => {
  try {
    const savedState = sessionStorage.getItem(CHATBOT_STORAGE_KEY)
    if (savedState) {
      const parsed = JSON.parse(savedState)

      // Validate the parsed state has expected structure
      if (parsed && typeof parsed === 'object') {
        // Ensure required fields exist with proper defaults
        return {
          chatId: parsed.chatId || null,
          chatConversationState: parsed.chatConversationState || 'normal',
          chatMessageHistory: Array.isArray(parsed.chatMessageHistory) ? parsed.chatMessageHistory : [],
          inputValue: parsed.inputValue || '',
          originalTools: parsed.originalTools || null,
          currentActionMessage: parsed.currentActionMessage || '',
          executeToolAction: parsed.executeToolAction || false,
          nameToId: parsed.nameToId || {
            sample: {},
            hpo: {},
            disease: {},
            panel: {},
          },
          idToName: parsed.idToName || {
            sample: {},
            hpo: {},
            disease: {},
            panel: {},
          },
          cache: parsed.cache || {
            sample: null,
            hpo: null,
            disease: null,
            panel: null,
          },
          // Dedicated disease mapping (separate from cache)
          diseaseNameToIdMapping: parsed.diseaseNameToIdMapping || {},
          diseaseIdToNameMapping: parsed.diseaseIdToNameMapping || {},
        }
      }
    }
  } catch (error) {
    console.error('Error loading chatbot state from sessionStorage:', error)
    // Clear corrupted session storage
    try {
      sessionStorage.removeItem(CHATBOT_STORAGE_KEY)
    } catch (clearError) {
      console.error('Error clearing corrupted session storage:', clearError)
    }
  }
  return null
}

// Load initial state from session storage
const savedState = loadFromSessionStorage()

const useChatbotStore = create((set, get) => ({
  // Initialize with saved state or defaults
  chatId: savedState?.chatId || null,
  setChatId: (chatId) => {
    set({ chatId })
    saveToSessionStorage({ ...get(), chatId })
  },

  chatConversationState: savedState?.chatConversationState || 'normal',
  setChatConversationState: (chatConversationState) => {
    set({ chatConversationState })
    saveToSessionStorage({ ...get(), chatConversationState })
  },

  isFilters: false,
  setIsFilters: (isFilters) => set({ isFilters }),

  currentActionMessage: savedState?.currentActionMessage || '',
  setCurrentActionMessage: (currentActionMessage) => {
    set({ currentActionMessage })
    saveToSessionStorage({ ...get(), currentActionMessage })
  },

  chatMessageHistory: savedState?.chatMessageHistory || [],
  setChatMessageHistory: (chatMessageHistory) => {
    set({ chatMessageHistory })
    saveToSessionStorage({ ...get(), chatMessageHistory })
  },

  executeToolAction: savedState?.executeToolAction || false,
  setExecuteToolAction: (executeToolAction) => {
    set({ executeToolAction })
    saveToSessionStorage({ ...get(), executeToolAction })
  },

  isFirstMessageLoading: false,
  setIsFirstMessageLoading: (isFirstMessageLoading) => set({ isFirstMessageLoading }),

  isBotResponding: false,
  setIsBotResponding: (isBotResponding) => set({ isBotResponding }),

  showChat: false,
  setShowChat: (showChat) => set({ showChat }),

  // Persistent input value
  inputValue: savedState?.inputValue || '',
  setInputValue: (inputValue) => {
    set({ inputValue })
    saveToSessionStorage({ ...get(), inputValue })
  },

  // Per chat id, store the original tools
  originalTools: savedState?.originalTools || null,
  setOriginalTools: (originalTools) => {
    set({ originalTools })
    saveToSessionStorage({ ...get(), originalTools })
  },

  // Cache-related state moved from ChatbotContext
  nameToId: savedState?.nameToId || {
    sample: {},
    hpo: {},
    disease: {},
    panel: {},
  },

  idToName: savedState?.idToName || {
    sample: {},
    hpo: {},
    disease: {},
    panel: {},
  },

  cache: savedState?.cache || {
    sample: null,
    hpo: null,
    disease: null,
    panel: null,
  },

  // Dedicated disease mapping state (Need refactor later)
  diseaseNameToIdMapping: savedState?.diseaseNameToIdMapping || {},
  diseaseIdToNameMapping: savedState?.diseaseIdToNameMapping || {},

  // Merge disease mapping from a list of selection objects into existing mapping
  // Each selection should ideally include { omim/id/key, disease/name/label }
  setDiseaseMappingFromSelections: (selections) => {
    try {
      console.log('setDiseaseMappingFromSelections: received selections:', selections)

      // If selections is empty, don't clear the existing mapping
      // This prevents clearing the mapping when clearSelections() is called
      if (!selections || selections.length === 0) {
        console.log('setDiseaseMappingFromSelections: skipping empty selections to preserve existing mapping')
        return
      }

      set((state) => {
        // Start with existing mappings to preserve previous selections
        const nameToId = { ...state.diseaseNameToIdMapping }
        const idToName = { ...state.diseaseIdToNameMapping }

        // Add new selections to existing mappings
        ;(selections || []).forEach((item) => {
          const id = item?.omim || item?.id || item?.key
          const name = item?.disease || item?.name || item?.label
          if (id && name) {
            nameToId[name] = id
            idToName[id] = name
          } else {
            console.log('setDiseaseMappingFromSelections: skipping item missing id or name:', item)
          }
        })

        console.log('setDiseaseMappingFromSelections: merging mappings:', { nameToId, idToName })

        const newState = {
          diseaseNameToIdMapping: nameToId,
          diseaseIdToNameMapping: idToName,
        }
        saveToSessionStorage({ ...state, ...newState })
        return newState
      })
    } catch (e) {
      console.log('setDiseaseMappingFromSelections: error while setting mapping', e)
    }
  },

  // Add/merge individual disease mappings
  addDiseaseMappings: (entries) => {
    try {
      console.log('addDiseaseMappings: received entries:', entries)
      const additions = Array.isArray(entries) ? entries : [entries]
      set((state) => {
        const nameToId = { ...state.diseaseNameToIdMapping }
        const idToName = { ...state.diseaseIdToNameMapping }
        additions.forEach((item) => {
          const id = item?.omim || item?.id || item?.key
          const name = item?.disease || item?.name || item?.label
          if (id && name) {
            nameToId[name] = id
            idToName[id] = name
          } else {
            console.log('addDiseaseMappings: skipping item missing id or name:', item)
          }
        })
        console.log('addDiseaseMappings: storing mappings:', { nameToId, idToName })
        const newState = {
          diseaseNameToIdMapping: nameToId,
          diseaseIdToNameMapping: idToName,
        }
        saveToSessionStorage({ ...state, ...newState })
        return newState
      })
    } catch (e) {
      console.log('addDiseaseMappings: error while adding mapping', e)
    }
  },

  // Remove a disease mapping by OMIM id
  removeDiseaseMappingById: (id) => {
    set((state) => {
      const nameToId = { ...state.diseaseNameToIdMapping }
      const idToName = { ...state.diseaseIdToNameMapping }
      const name = idToName[id]
      if (name) {
        delete idToName[id]
        delete nameToId[name]
        console.log('removeDiseaseMappingById: removed mapping for id:', id, 'name:', name)
      } else {
        console.log('removeDiseaseMappingById: id not found in mapping:', id)
      }
      const newState = {
        diseaseNameToIdMapping: nameToId,
        diseaseIdToNameMapping: idToName,
      }
      saveToSessionStorage({ ...state, ...newState })
      return newState
    })
  },

  clearDiseaseMapping: () => {
    set((state) => {
      console.log('clearDiseaseMapping: clearing all disease mappings')
      const newState = { diseaseNameToIdMapping: {}, diseaseIdToNameMapping: {} }
      saveToSessionStorage({ ...state, ...newState })
      return newState
    })
  },

  // Cache functions moved from ChatbotContext
  updateCache: (type, data) => {
    if (!data || !Array.isArray(data)) return

    set((state) => {
      // Update the cache state itself
      const newCache = {
        ...state.cache,
        [type]: data,
      }

      // Update nameToId and idToName mappings
      const newNameToId = { ...state.nameToId }
      const newIdToName = { ...state.idToName }
      const newNames = {}
      const newIds = {}

      data.forEach((item) => {
        if (item.name && item.id) {
          newNames[item.name] = item.id
          newIds[item.id] = item.name
        }
      })

      newNameToId[type] = { ...newNameToId[type], ...newNames }
      newIdToName[type] = { ...newIdToName[type], ...newIds }

      const newState = {
        cache: newCache,
        nameToId: newNameToId,
        idToName: newIdToName,
      }

      // Save to session storage
      saveToSessionStorage({ ...state, ...newState })

      return newState
    })
  },

  getCachedData: (type) => {
    return get().cache[type]
  },

  // Clear session storage when starting a new chat
  clearSessionStorage: () => {
    try {
      sessionStorage.removeItem(CHATBOT_STORAGE_KEY)
      // Reset chat-specific state but preserve cache data and disease mappings
      const currentState = get()
      set({
        chatId: null,
        chatConversationState: 'normal',
        currentActionMessage: '',
        chatMessageHistory: [],
        executeToolAction: false,
        inputValue: '',
        originalTools: null,
        // Preserve cache data and disease mappings - these should persist across all chats in the session
        nameToId: currentState.nameToId,
        idToName: currentState.idToName,
        cache: currentState.cache,
        diseaseNameToIdMapping: currentState.diseaseNameToIdMapping,
        diseaseIdToNameMapping: currentState.diseaseIdToNameMapping,
      })
    } catch (error) {
      console.error('Error clearing chatbot session storage:', error)
    }
  },

  // Clear all state including UI state (for complete reset)
  clearAllState: (preserveCache = false) => {
    try {
      sessionStorage.removeItem(CHATBOT_STORAGE_KEY)
      const currentState = get()
      set({
        chatId: null,
        chatConversationState: 'normal',
        isFilters: false,
        currentActionMessage: '',
        chatMessageHistory: [],
        executeToolAction: false,
        isFirstMessageLoading: false,
        isBotResponding: false,
        showChat: false,
        inputValue: '',
        originalTools: null,
        // Optionally preserve cache data
        nameToId: preserveCache
          ? currentState.nameToId
          : {
              sample: {},
              hpo: {},
              disease: {},
              panel: {},
            },
        idToName: preserveCache
          ? currentState.idToName
          : {
              sample: {},
              hpo: {},
              disease: {},
              panel: {},
            },
        cache: preserveCache
          ? currentState.cache
          : {
              sample: null,
              hpo: null,
              disease: null,
              panel: null,
            },
        // Preserve disease mappings when preserving cache
        diseaseNameToIdMapping: preserveCache ? currentState.diseaseNameToIdMapping : {},
        diseaseIdToNameMapping: preserveCache ? currentState.diseaseIdToNameMapping : {},
      })
    } catch (error) {
      console.error('Error clearing all chatbot state:', error)
    }
  },

  messagesContainerRef: { current: null },
  // Helper function to scroll to the bottom of the messages container
  scrollToBottom: () => {
    const { messagesContainerRef } = get()
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
    }
  },
}))

export default useChatbotStore
